import {
  EAccountRoles,
  EAccountStatuses,
  IAccountBaseWithId,
  IAccountUserResponseDto,
} from './account.type'
import { E2FAMethod } from './auth.type'
import { IBaseDtoType, IPrivateKeyExpose } from './common.type'
import { ILanguageBaseWithId } from './language.type'
import { ICustomIconFile } from './media.type'
import { IUserWalletBase } from './user-wallet.type'
import { IWhiteLabelForm } from './white-label-partners.type'

export interface IProfileUserBase {
  name: string | null
  email: string
  mobile: string | null
  image: string
  image_small: string | null
  image_medium: string | null
}

export interface IProfileUserBaseWithId extends IProfileUserBase, IBaseDtoType {}

export interface IProfileUserForm {
  email: string
  name: string | null
  mobile: string | null
  image: ICustomIconFile
  image_small: string | null
  image_medium: string | null
  wallets: Omit<IUserWalletBase, 'privateKey'>[]
}

export interface IProfileUserUpdateFormDto extends IProfileUserForm {
  oldIcon: string
  image_small: string | null
  image_medium: string | null
}

export interface IProfileUser {
  image?: {
    icon: string
    file: File | null
  }
  name?: string
  email?: string
  mobile?: string
  oldIcon?: string | null
  timezone?: string | null
}

export interface IProfileUserWithId extends IProfileUser {
  id: string
}

export interface IProfileUserUpdateDto {
  name: string | null
  image: string | null
  image_small: string | null
  image_medium: string | null
  mobile: string | null
}

export interface IProfileUserDtoWithId extends IProfileUserBaseWithId {
  email: string
  roles: EUserRoles[]
  agreement: boolean
  activated: boolean
  timezone: string
  show_welcome: boolean
}

export type IProfileUserResponse = IProfileUserDtoWithId & {
  roles: EUserRoles[]
  accounts: IAccountUser[]
  dark_mode: boolean
  auto_theme: boolean
  suspended: boolean
  wallets: Omit<IUserWalletBase, 'privateKey'>[]
  language: ILanguageBaseWithId
  two_factor_required: boolean
  two_factor_method: E2FAMethod | null
  created_at: string
  white_label: IWhiteLabelForm
  customer: boolean
  referral_id: string
}

export interface IProfileUsersResponse {
  count: number
  data: Array<IProfileUserResponse>
}

export interface IProfileUserWithAccountResponse extends IProfileUserResponse {
  account: IAccountUserResponseDto | null
}

export interface IUpdateUserRolesDto {
  roles: EUserRoles[]
  id: string
}

export interface IUpdateUserDefaultThemeDto {
  auto_theme: boolean
}

export enum EUserRoles {
  user = 'user',
  superadmin = 'superadmin',
}

export interface IChangeUserPasswordDto {
  oldPassword: string
  newPassword: string
}

export interface IChangeUserPasswordRespDto {
  message: string
  change_password_token: string
}

export interface IChangeUserPasswordFormProps {
  oldPassword: string
  newPassword: string
  newPasswordConfirm: string
}

export interface ISetUserTimezoneDto {
  timezone: string
}

export interface IAccountUser {
  id: string
  roles: EAccountRoles[]
  account: IAccountBaseWithId
  status: EAccountStatuses
}

export interface IUserSuspensionDto {
  userId: string
  suspended: boolean
}

export interface IUserExposePrivateKeyRespDto extends IPrivateKeyExpose {
  privateKey: string
  address: string
  is_custodial: boolean
}

export interface IUserPrivateKeyVerifyDto {
  code: string
}

export interface IUser2FASwitchRespDto {
  message: string
  totpUri: string
}

export enum E2FATab {
  Email,
  Totp,
  None,
}

export enum E2FAMethodTab {
  Disable,
  Switch,
  Home,
}
export interface IUserByIdRawRespDto
  extends Pick<
    IProfileUserBaseWithId,
    'name' | 'id' | 'image' | 'image_small' | 'image_medium' | 'email'
  > {}

export interface IUserProvider {
  id: string
  created_at: string
  provider: EUserProviders
  provider_id: string
  provider_identifier: string
}

export interface IUserProviderResponse extends IUserProvider {}

export interface IUserRefferal {
  id: string
  created_at: Date
  email: string
  name: string | null
  numberOfScans: number
  default_wallet: string
}

export interface IUserRefferalResponse extends IUserRefferal {}

export enum EUserProviders {
  Google = 'google',
  Linkedin = 'linkedin',
  X = 'x',
  Apple = 'apple',
  Microsoft = 'microsoft',
}
